import { cn } from '@discover-lib/utils';
import StarIcon from '@discover-ui/icons/star-ds.svg';
import { type SVGAttributes, useId } from 'react';

const MIN = 0;
const MAX = 5;
const MASK_SIZE = 24;
const STAR_PATH =
  'M12 .587l3.668 7.568 8.332 1.151-6.064 5.828 1.48 8.279-7.416-3.967-7.417 3.967 1.481-8.279-6.064-5.828 8.332-1.151z';
const STARS = Array(MAX).fill(null);

export type StarsProps = React.ComponentPropsWithoutRef<'div'> & {
  count?: number | null;
  size?: number;
  gap?: number;
  outlineColor?: SVGAttributes<SVGPathElement>['stroke'];
  /** Fill color for the unfilled portion of each star. */
  backgroundColor?: SVGAttributes<SVGPathElement>['fill'];
  /** Rounded design-system star. The stroke matches the fill, so there is no contrasting border. */
  rounded?: boolean;
};

export default function Stars({
  count,
  className,
  size = 16,
  gap = 2.5,
  outlineColor = 'currentColor',
  backgroundColor = 'white',
  rounded = false,
  ...props
}: StarsProps) {
  const boundedCount = Math.max(MIN, Math.min(MAX, count || 0));
  const safeSize = size > 0 ? size : 16;
  const safeGap = Math.max(gap, 0);
  const width = safeSize * MAX + safeGap * (MAX - 1);
  const gradientId = useId().replaceAll(':', '');

  return (
    <div
      {...props}
      style={{ width: `${width}px`, height: `${safeSize}px`, gap: `${safeGap}px` }}
      className={cn(
        'relative flex items-center justify-start bg-white text-yellow-400',
        className
      )}>
      {STARS.map((_, index) => {
        const fillAmount = Math.max(MIN, Math.min(1, boundedCount - index));

        if (rounded) {
          return (
            <span
              key={index}
              className="relative shrink-0"
              style={{ width: safeSize, height: safeSize }}>
              {fillAmount < 1 && (
                <StarIcon
                  aria-hidden
                  className="absolute inset-0"
                  height={safeSize}
                  style={{ color: backgroundColor }}
                  width={safeSize}
                />
              )}
              {fillAmount > 0 && (
                <span
                  className="absolute inset-y-0 left-0 overflow-hidden"
                  style={{ width: `${fillAmount * 100}%` }}>
                  <StarIcon
                    aria-hidden
                    className="absolute left-0 top-0 max-w-none"
                    height={safeSize}
                    width={safeSize}
                  />
                </span>
              )}
            </span>
          );
        }

        const starGradientId = `${gradientId}-${index}`;
        let fill = backgroundColor;
        if (fillAmount === 1) {
          fill = 'currentColor';
        } else if (fillAmount > 0) {
          fill = `url(#${starGradientId})`;
        }

        return (
          <svg
            key={index}
            aria-hidden="true"
            className="shrink-0 overflow-visible"
            height={safeSize}
            width={safeSize}
            viewBox={`0 0 ${MASK_SIZE} ${MASK_SIZE}`}>
            {fillAmount > 0 && fillAmount < 1 && (
              <defs>
                <linearGradient id={starGradientId} x1="0%" x2="100%" y1="0%" y2="0%">
                  <stop offset={`${fillAmount * 100}%`} stopColor="currentColor" />
                  <stop offset={`${fillAmount * 100}%`} stopColor={backgroundColor} />
                </linearGradient>
              </defs>
            )}
            <path
              d={STAR_PATH}
              fill={fill}
              stroke={outlineColor}
              strokeWidth={1.5}
              strokeLinejoin="round"
            />
          </svg>
        );
      })}
    </div>
  );
}
